import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiArrowLeft, FiUser, FiSave, FiX, FiBell, FiBellOff } from 'react-icons/fi';
import Toast from './Toast';

import { API_URL, getAssetUrl } from '../config';

function Profile() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');

  useEffect(() => {
    loadProfile();
  }, []);

  // Check push notification support and current state
  useEffect(() => {
    const checkPushSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setPushSupported(supported);

      if (supported) {
        setPushPermission(Notification.permission);

        // Check if already subscribed
        const stored = localStorage.getItem(`push-enabled-${user?.id}`);
        if (stored === 'true' && Notification.permission === 'granted') {
          setPushEnabled(true);
        }
      }
    };
    checkPushSupport();
  }, [user?.id]);

  // URL-safe base64 to Uint8Array (for applicationServerKey)
  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const handleTogglePush = async () => {
    if (!pushSupported) {
      setToast({ message: 'Push notifications are not supported in this browser', type: 'error' });
      return;
    }

    setPushLoading(true);

    try {
      if (pushEnabled) {
        // ── Unsubscribe ──
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }

        await axios.delete(`${API_URL}/push/subscribe`);

        setPushEnabled(false);
        localStorage.setItem(`push-enabled-${user?.id}`, 'false');
        setToast({ message: 'Push notifications disabled', type: 'success' });
      } else {
        // ── Subscribe ──
        // 1. Request browser permission
        const permission = await Notification.requestPermission();
        setPushPermission(permission);

        if (permission !== 'granted') {
          setToast({
            message: 'Notification permission denied. Please allow notifications in your browser settings.',
            type: 'error',
          });
          setPushLoading(false);
          return;
        }

        // 2. Get VAPID public key from server
        const keyResp = await axios.get(`${API_URL}/push/vapid-key`);
        const vapidPublicKey = keyResp.data.publicKey;

        // 3. Subscribe via Push API
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });

        // 4. Send subscription to backend
        await axios.post(`${API_URL}/push/subscribe`, { subscription });

        setPushEnabled(true);
        localStorage.setItem(`push-enabled-${user?.id}`, 'true');
        setToast({ message: 'Push notifications enabled!', type: 'success' });
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      setToast({
        message: err.response?.data?.error || 'Error toggling push notifications',
        type: 'error',
      });
    } finally {
      setPushLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/${user?.id}`);
      const userData = response.data.user;
      setFormData({
        username: userData.username,
        email: userData.email,
        password: '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
      });
      setProfileImage(userData.profileImage);
      setPreviewImage(userData.profileImage ? getImageUrl(userData.profileImage) : null);
    } catch (error) {
      console.error('Error loading profile:', error);
      setToast({ message: 'Error loading profile', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.email);
      if (formData.password) {
        formDataToSend.append('password', formData.password);
      }
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      if (profileImage && profileImage instanceof File) {
        formDataToSend.append('profileImage', profileImage);
      }

      await axios.put(`${API_URL}/users/${user?.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setToast({ message: 'Profile updated successfully!', type: 'success' });
      
      // Reload user data
      const response = await axios.get(`${API_URL}/auth/me`);
      if (response.data.user) {
        // Update user in context without reloading
        window.location.reload(); // Simple reload to refresh all data
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setToast({ message: error.response?.data?.error || 'Error updating profile', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-900 font-semibold">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-5">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium">Manage your profile information</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <div className="glass-card rounded-2xl shadow-xl p-5 sm:p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-white/20">
              <div className="flex-shrink-0">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="Profile"
                    className="h-24 w-24 sm:h-32 sm:w-32 rounded-full object-cover ring-4 ring-white/50 shadow-lg"
                  />
                ) : (
                  <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-4xl sm:text-5xl font-semibold ring-4 ring-white/50 shadow-lg">
                    {formData.firstName ? formData.firstName[0] : formData.username[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
                  Profile Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196] file:shadow-md"
                />
                <p className="text-xs sm:text-sm text-gray-700 mt-1 font-medium">JPG, PNG or GIF. Max size 5MB</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 font-medium placeholder-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 font-medium placeholder-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password (leave blank to keep current)
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent"
              />
            </div>

            {/* ── Push Notifications ── */}
            <div className="pt-4 border-t border-white/20">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FiBell className="text-[#3c79bb]" />
                Push Notifications
              </h3>
              {!pushSupported ? (
                <p className="text-sm text-gray-600 font-medium">
                  Push notifications are not supported in this browser.
                </p>
              ) : pushPermission === 'denied' ? (
                <p className="text-sm text-red-600 font-medium">
                  Notifications are blocked. Please enable them in your browser settings and reload.
                </p>
              ) : (
                <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-300/60 shadow-sm">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">
                      {pushEnabled ? 'Notifications enabled' : 'Notifications disabled'}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {pushEnabled
                        ? 'You will receive alerts when new chat messages arrive.'
                        : 'Enable to get notified of new chat messages.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTogglePush}
                    disabled={pushLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 shadow-sm ${
                      pushEnabled
                        ? 'bg-green-100/90 text-green-800 border border-green-400 hover:bg-green-200/90'
                        : 'bg-white/70 text-gray-700 border border-gray-300 hover:bg-white/90'
                    } disabled:opacity-50`}
                  >
                    {pushLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    ) : pushEnabled ? (
                      <FiBell className="text-green-600" />
                    ) : (
                      <FiBellOff className="text-gray-500" />
                    )}
                    {pushLoading ? '' : pushEnabled ? 'On' : 'Off'}
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#3c79bb] text-white px-6 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40"
              >
                <FiSave className="text-lg" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 glass text-gray-900 px-6 py-3 rounded-xl hover:glass-strong transition-all duration-200 font-bold flex items-center justify-center gap-2 border border-white/30"
              >
                <FiX className="text-lg" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Profile;
